import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/BrandMark";
import { waChat, waMsg } from "@/components/site/WhatsAppFloat";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { renderScriptTitle } from "@/components/site/scriptTitle";
import { Mail, Instagram, Laptop, Send, Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ content }) {
  const infos = [
    { icon: Mail, label: "E-mail", value: content?.email || "contato@tacilaine.com.br" },
    { icon: Instagram, label: "Instagram", value: content?.instagram || "@tacilaine.psi" },
    ...(content?.whatsapp ? [{ icon: WhatsAppIcon, label: "WhatsApp", value: content.whatsapp }] : []),
    { icon: Laptop, label: "Modalidade", value: "Atendimento online" },
  ];
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Preencha nome, e-mail e uma mensagem.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensagem enviada! Em breve entrarei em contato. 💚");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-white border border-[#5B6E5B]/15 px-4 py-3 font-body text-[#2C332A] placeholder:text-[#A8B0A2] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[#C87A5E] focus:ring-2 focus:ring-[#C87A5E]/25";

  return (
    <section id="contato" className="relative bg-[#F5F0E6] py-24 sm:py-32 overflow-hidden" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <Reveal>
            <span className="font-body text-xs tracking-[0.4em] uppercase text-[#C87A5E]">Contato</span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-[#2C332A] leading-tight text-balance">
              {renderScriptTitle(content?.contact_title || "Agenda *aberta!*", "font-script text-[#5B6E5B] text-6xl sm:text-7xl")}
            </h2>
            <p className="mt-5 font-body text-[#4A5D4E] leading-relaxed max-w-md">
              {content?.contact_intro || "Vamos juntas(os) cuidar da sua saúde mental? Deixe sua mensagem e retornarei para combinarmos o seu primeiro atendimento online."}
            </p>
            <Sprig className="mt-6 w-28 h-9" color="#5B6E5B" />
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {infos.map((it, i) => {
              const waUrl = it.label === "WhatsApp" ? waChat(it.value, waMsg(content)) : null;
              const inner = (
                <>
                  <div className={`grid place-items-center w-11 h-11 rounded-full ${waUrl ? "bg-[#25D366]/15" : "bg-[#5B6E5B]/10"}`}>
                    <it.icon className={`w-5 h-5 ${waUrl ? "text-[#1EBE5A]" : "text-[#5B6E5B]"}`} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-body text-[11px] uppercase tracking-widest text-[#7A8A7A]">{it.label}</p>
                    <p className="font-body text-sm text-[#2C332A] inline-flex items-center gap-1.5">
                      {it.value}
                      {waUrl && <ArrowUpRight className="w-3.5 h-3.5 text-[#1EBE5A]" />}
                    </p>
                  </div>
                </>
              );
              return (
                <Reveal key={it.label} delay={0.08 * i}>
                  {waUrl ? (
                    <a href={waUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"
                      className="flex items-center gap-4 rounded-2xl bg-white/70 border border-[#25D366]/35 p-4 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:border-[#25D366]">
                      {inner}
                    </a>
                  ) : (
                    <div className="flex items-center gap-4 rounded-2xl bg-white/70 border border-[#5B6E5B]/10 p-4">
                      {inner}
                    </div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15}>
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl bg-white p-7 sm:p-10 shadow-[0_20px_60px_rgba(91,110,91,0.1)] border border-[#5B6E5B]/10"
          >
            <div className="space-y-5">
              <div>
                <label className="font-body text-sm text-[#4A5D4E] mb-1.5 block">Nome</label>
                <input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Seu nome"
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="font-body text-sm text-[#4A5D4E] mb-1.5 block">E-mail</label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="voce@email.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-[#4A5D4E] mb-1.5 block">Telefone (opcional)</label>
                  <input
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(00) 00000-0000"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="font-body text-sm text-[#4A5D4E] mb-1.5 block">Mensagem</label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Conte um pouco sobre o que você procura..."
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#5B6E5B] px-8 py-4 text-[#FDFBF7] transition-[transform,background-color] duration-300 hover:bg-[#4A5D4E] hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.6} />
                )}
                <span className="font-body">{loading ? "Enviando..." : "Enviar mensagem"}</span>
              </button>
              <p className="text-center font-body text-xs text-[#7A8A7A]">
                Suas informações são tratadas com ética e sigilo.
              </p>
            </div>
          </motion.form>
        </Reveal>
      </div>
    </section>
  );
}
